import { useNavigate } from 'react-router';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { useTableContext } from '../../../../context/table-context';
import { CARD_STATUS_PAYMENT_METHODS } from '../../../../constants/consumers';
import { useMemo } from 'react';

function DashboardPaymentMethodWidget({
  title,
  paymentMethods,
  paymentMethodKey,
  consumers,
  calculate,
  handleClick,
}) {
  return (
    <div className="bo-border-container rounded m-1 bo-min-w-200">
      <p className="text-capitalize bo-badge">{title}</p>
      <div className="d-flex align-items-center justify-content-between">
        <p className="text-center h6 me-3">Active</p>
        {paymentMethods &&
        paymentMethods[`${paymentMethodKey}Active`] &&
        paymentMethods[`${paymentMethodKey}Active`].count !== null &&
        paymentMethods[`${paymentMethodKey}Active`].count !== undefined &&
        !!consumers.active ? (
          <div className="d-flex align-items-center">
            <Button
              className="bo-button-inline-link p-0 me-1 d-flex"
              onClick={() =>
                handleClick(
                  paymentMethods[`${paymentMethodKey}Active`].list,
                  `Dashboard → Payment Methods → ${title} → Active`,
                )
              }
            >
              <p className="text-center h6">
                {calculate(paymentMethods[`${paymentMethodKey}Active`].count, consumers.active)}
              </p>
            </Button>
            <span className="bo-text-sm mb-2">
              ({paymentMethods[`${paymentMethodKey}Active`].count}/{consumers.active})
            </span>
          </div>
        ) : (
          <p className="h6">N/A</p>
        )}
      </div>
      <div className="d-flex align-items-center justify-content-between">
        <p className="text-center h6 me-3">Billable</p>
        {paymentMethods &&
        paymentMethods[`${paymentMethodKey}Billable`] &&
        paymentMethods[`${paymentMethodKey}Billable`].count !== null &&
        paymentMethods[`${paymentMethodKey}Billable`].count !== undefined &&
        !!consumers.billable ? (
          <div className="d-flex align-items-center">
            <Button
              className="bo-button-inline-link p-0 me-1 d-flex"
              onClick={() =>
                handleClick(
                  paymentMethods[`${paymentMethodKey}Billable`].list,
                  `Dashboard → Payment Methods → ${title} → Billable`,
                )
              }
            >
              <p className="text-center h6">
                {calculate(paymentMethods[`${paymentMethodKey}Billable`].count, consumers.billable)}
              </p>
            </Button>
            <span className="bo-text-sm mb-2">
              ({paymentMethods[`${paymentMethodKey}Billable`].count}/{consumers.billable})
            </span>
          </div>
        ) : (
          <p className="h6">N/A</p>
        )}
      </div>
    </div>
  );
}

function DashboardPaymentMethods({ paymentMethods, consumers }) {
  const navigate = useNavigate();

  const { clearTableFilters, setSelectedConsumerIdsFilter } = useTableContext();

  const calculate = (dividend, divisor) =>
    `${Number((dividend / divisor) * 100).toLocaleString('en-US', {
      maximumFractionDigits: 1,
    })}% `;

  const cardsByStatus = useMemo(
    () =>
      paymentMethods?.cardsByStatus.reduce((acc, val, _, arr) => {
        acc[val.cardStatus] = {
          text: CARD_STATUS_PAYMENT_METHODS[val.cardStatus]?.text,
          color: CARD_STATUS_PAYMENT_METHODS[val.cardStatus]?.color,
          count: acc[val.cardStatus] ? acc[val.cardStatus].count + 1 : 1,
          total: arr.length,
          list: acc[val.cardStatus]
            ? [...acc[val.cardStatus].list, val.consumerId]
            : [val.consumerId],
        };

        return acc;
      }, {}) || {},
    [paymentMethods],
  );

  const handleClick = (consumerIds, message) => {
    clearTableFilters({
      tableName: 'consumers',
      initSortBy: 'createDate',
      initSortOrder: 'desc',
      initRowsPerPage: 25,
    });
    setSelectedConsumerIdsFilter({ consumerIds: consumerIds.join(','), message });
    navigate('/consumers');
  };

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between">Payment Methods</Card.Header>
      <Card.Body className="bo-min-h-100">
        <div className="d-flex justify-content-center flex-wrap">
          <DashboardPaymentMethodWidget
            title="Connected Accounts"
            paymentMethods={paymentMethods}
            paymentMethodKey="connected"
            consumers={consumers}
            calculate={calculate}
            handleClick={handleClick}
          />
          <DashboardPaymentMethodWidget
            title="Cards On File"
            paymentMethods={paymentMethods}
            paymentMethodKey="cardOnFile"
            consumers={consumers}
            calculate={calculate}
            handleClick={handleClick}
          />
          <DashboardPaymentMethodWidget
            title="Cards Required"
            paymentMethods={paymentMethods}
            paymentMethodKey="cardRequired"
            consumers={consumers}
            calculate={calculate}
            handleClick={handleClick}
          />
          <div className="bo-border-container rounded m-1 bo-min-w-300">
            <p className="text-capitalize bo-badge">Cards By Status</p>
            {Object.entries(cardsByStatus).length > 0 ? (
              Object.entries(cardsByStatus)
                .sort(
                  ([a], [b]) =>
                    Object.keys(CARD_STATUS_PAYMENT_METHODS).indexOf(a) -
                    Object.keys(CARD_STATUS_PAYMENT_METHODS).indexOf(b),
                )
                .map(([status, value]) => (
                  <div key={status} className="d-flex align-items-center justify-content-between">
                    <Badge bg={value.color} className="bo-inline-badge">
                      {value.text}
                    </Badge>
                    <div className="d-flex align-items-center">
                      <Button
                        className="bo-button-inline-link p-0 me-1 d-flex"
                        onClick={() =>
                          handleClick(
                            value.list,
                            `Dashboard → Payment Methods → Cards By Status → ${value.text}`,
                          )
                        }
                      >
                        <p className="text-center h6">{calculate(value.count, value.total)}</p>
                      </Button>
                      <span className="bo-text-sm mb-2">{`(${value.count}/${value.total})`}</span>
                    </div>
                  </div>
                ))
            ) : (
              <div className="d-flex justify-content-center">
                <p className="text-center h6">N/A</p>
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default DashboardPaymentMethods;
